import {useEffect, useState} from 'react';

import {useScreenSize} from 'app/general/mediaqueries';
import {
  useSidebarIsOpenState,
  useToggleSidebarIsOpen,
  useToggleWidgetIsOpen,
  useWidgetIsOpenState,
} from 'app/settings/hooks';
import {
  VERSIONS_SIDEBAR_TOGGABLE,
  type WidgetIsOpenPayloadType,
} from 'app/settings/slice';

import type {LeftSidebarElementType} from 'app/components/general/DetailPage/LeftSidebar/structure/types';
import type {StickySidebarElementType} from 'app/components/general/DetailPage/RightSidebar/structure/types';
import type {
  CurrentItemType,
  DetailPageItemType,
} from 'app/components/general/DetailPage/types';
import type {WithRouterProps} from 'react-router';

export function useAllOpenWidgets<ItemType extends DetailPageItemType>({
  contentType,
  objectId,
  widgets,
}: Partial<Omit<WidgetIsOpenPayloadType, 'widgetName'>> & {
  widgets: Array<StickySidebarElementType<ItemType>>;
}) {
  const widgetStatus = {};
  widgets.forEach((widget) => {
    widgetStatus[widget.sidebarName] = useWidgetIsOpenState({
      contentType,
      objectId,
      widgetName: widget.sidebarName,
    });
  });

  return widgets.filter((widget) => widgetStatus[widget.sidebarName]);
}

function useRightWidgetToggleState<ItemType extends DetailPageItemType>(
  object: DetailPageItemType | undefined,
  availableWidgets: Array<StickySidebarElementType<ItemType>>,
  maybeOpenRightSidebar: StickySidebarElementType<ItemType> | undefined
) {
  const currentlyOpenRightWidgets = useAllOpenWidgets({
    objectId: object?.id,
    contentType: object?.content_type,
    widgets: availableWidgets,
  });

  const toggleWidgetIsOpen = useToggleWidgetIsOpen();

  useEffect(() => {
    if (
      maybeOpenRightSidebar &&
      object != null &&
      !currentlyOpenRightWidgets.includes(maybeOpenRightSidebar)
    ) {
      toggleWidgetIsOpen({
        objectId: object.id,
        contentType: object.content_type,
        widgetName: maybeOpenRightSidebar.sidebarName,
      });
    }
  }, []);

  return {
    currentlyOpenRightWidgets,
    closeRightWidget: (widget: StickySidebarElementType<ItemType>) => {
      if (object != null && currentlyOpenRightWidgets.includes(widget)) {
        toggleWidgetIsOpen({
          objectId: object.id,
          contentType: object.content_type,
          widgetName: widget.sidebarName,
        });
      }
    },
  };
}

function useLeftPanelToggleState<ItemType extends DetailPageItemType>(
  object: CurrentItemType<ItemType> | undefined,
  router: WithRouterProps['router'],
  maybeOpenLeftPanel: LeftSidebarElementType<ItemType> | undefined
) {
  const sidebarVersionIsOpen = useSidebarIsOpenState({
    objectId: object?.id,
    contentType: object?.content_type,
    widgetName: VERSIONS_SIDEBAR_TOGGABLE,
  });
  const toggleSidebarIsOpen = useToggleSidebarIsOpen();

  return {
    currentlyOpenLeftPanel:
      maybeOpenLeftPanel != null
        ? // 'showVersions' panel can maybe be in "toggled" state - which
          // means that it might actually be closed - even if
          // ``maybeOpenLeftPanel`` variable is not ``undefined``
          maybeOpenLeftPanel.sidebarName !== 'showVersions'
          ? maybeOpenLeftPanel
          : sidebarVersionIsOpen
          ? maybeOpenLeftPanel
          : undefined
        : undefined,
    closeLeftPanel: (panel: LeftSidebarElementType<ItemType>) => {
      if (object != null)
        if (panel.sidebarName === 'showVersions') {
          if (sidebarVersionIsOpen) {
            toggleSidebarIsOpen({
              objectId: object.id,
              contentType: object.content_type,
              widgetName: VERSIONS_SIDEBAR_TOGGABLE,
            });
          }
        } else {
          router.replace(object.html_url);
        }
    },
  };
}

function listOfPanelsToNames<ItemType extends DetailPageItemType>(
  panels: Array<
    StickySidebarElementType<ItemType> | LeftSidebarElementType<ItemType>
  >
) {
  return panels.map(({sidebarName}) => sidebarName).join('|');
}

function mergeListsPreservingOrder<ItemType extends DetailPageItemType>(
  panelsFromPreviousRender: Array<
    LeftSidebarElementType<ItemType> | StickySidebarElementType<ItemType>
  >,
  panelsFromThisRender: Array<
    | LeftSidebarElementType<ItemType>
    | StickySidebarElementType<ItemType>
    | undefined
  >
) {
  // Copy all of the old panels to openSidebarsInOrder - to enforce the element order
  const openSidebarsInOrder: Array<
    LeftSidebarElementType<ItemType> | StickySidebarElementType<ItemType>
  > = panelsFromPreviousRender.filter((lastOpenSidebar) => {
    return panelsFromThisRender.includes(lastOpenSidebar);
  });

  // Push the newly created panels at the end of the openSidebarsInOrder list
  panelsFromThisRender.forEach((Sidebar) => {
    if (Sidebar != null && !openSidebarsInOrder.includes(Sidebar)) {
      openSidebarsInOrder.push(Sidebar);
    }
  });

  return openSidebarsInOrder;
}

function useOpenPanelsInOrder<ItemType extends DetailPageItemType>(
  maybeOpenLeftPanel: LeftSidebarElementType<ItemType> | undefined,
  router: WithRouterProps['router'],
  object: CurrentItemType<ItemType> | undefined,
  availableWidgets: Array<StickySidebarElementType<ItemType>>,
  maybeOpenRightSidebar: StickySidebarElementType<ItemType> | undefined
) {
  const [allOpenPanelsInOrder, setAllOpenPanelsInOrder] = useState<
    Array<LeftSidebarElementType<ItemType> | StickySidebarElementType<ItemType>>
  >(maybeOpenRightSidebar ? [maybeOpenRightSidebar] : []);

  const {currentlyOpenRightWidgets, closeRightWidget} =
    useRightWidgetToggleState(object, availableWidgets, maybeOpenRightSidebar);
  const {currentlyOpenLeftPanel, closeLeftPanel} = useLeftPanelToggleState(
    object,
    router,
    maybeOpenLeftPanel
  );

  const closeSidebars = (
    sidebarsToClose: Array<
      LeftSidebarElementType<ItemType> | StickySidebarElementType<ItemType>
    >
  ) => {
    sidebarsToClose.forEach((sidebarOrWidget) => {
      if (sidebarOrWidget.type === 'right') {
        closeRightWidget(sidebarOrWidget);
      } else {
        closeLeftPanel(sidebarOrWidget);
      }
    });
  };

  const openSidebarsInOrder = mergeListsPreservingOrder(allOpenPanelsInOrder, [
    ...currentlyOpenRightWidgets,
    currentlyOpenLeftPanel,
  ]);

  if (
    listOfPanelsToNames(allOpenPanelsInOrder) !==
    listOfPanelsToNames(openSidebarsInOrder)
  ) {
    setAllOpenPanelsInOrder(openSidebarsInOrder);
  }

  return {
    allOpenPanelsInOrder: openSidebarsInOrder,
    closeSidebars,
  };
}

const SCREEN_SIZE_LIMITS = {
  sm: 1,
  md: 1,
  lg: 2,
  xl: Infinity,
};

const SCREEN_SIZE_LIMITS_WITH_FULLSCREEN = {
  sm: SCREEN_SIZE_LIMITS.sm,
  md: SCREEN_SIZE_LIMITS.md + 1,
  lg: SCREEN_SIZE_LIMITS.lg + 1,
  xl: Infinity,
};

export function usePanelsOnDifferentPageSizes<
  ItemType extends DetailPageItemType
>(
  CurrentLeftSidebar: LeftSidebarElementType<ItemType> | undefined,
  router: WithRouterProps['router'],
  object: CurrentItemType<ItemType> | undefined,
  availableWidgets: Array<StickySidebarElementType<ItemType>>,
  CurrentRightSidebar: StickySidebarElementType<ItemType> | undefined
) {
  const {allOpenPanelsInOrder, closeSidebars} = useOpenPanelsInOrder(
    CurrentLeftSidebar,
    router,
    object,
    availableWidgets,
    CurrentRightSidebar
  );

  const [fullscreenWidget, setFullscreenWidget] = useState<
    | null
    | StickySidebarElementType<DetailPageItemType>
    | LeftSidebarElementType<DetailPageItemType>
  >(null);

  const {currentScreenSize} = useScreenSize();

  useEffect(() => {
    if (currentScreenSize != null) {
      if (
        allOpenPanelsInOrder.length >= SCREEN_SIZE_LIMITS[currentScreenSize]
      ) {
        const panelLimit =
          fullscreenWidget == null
            ? SCREEN_SIZE_LIMITS[currentScreenSize]
            : SCREEN_SIZE_LIMITS_WITH_FULLSCREEN[currentScreenSize];

        if (allOpenPanelsInOrder.length > panelLimit) {
          // close all other sidebars that were open before. Leave just the
          // panelLimit amount
          closeSidebars(allOpenPanelsInOrder.slice(0, -panelLimit));
        }
      }
    }
  }, [currentScreenSize, listOfPanelsToNames(allOpenPanelsInOrder)]);

  useEffect(() => {
    if (currentScreenSize === 'sm' && CurrentRightSidebar == null) {
      // on load hide all sidebars so the central content can shine bright
      closeSidebars(allOpenPanelsInOrder);
    }

    setFullscreenAndClosePrevious(CurrentRightSidebar ?? null);
  }, []);

  useEffect(() => {
    if (
      fullscreenWidget != null &&
      !allOpenPanelsInOrder.includes(fullscreenWidget)
    ) {
      // Panel was closed indirectly - but ``fullscreen`` mode was not notified about it
      // (this is currently happening when the left versions panel is toggled)
      setFullscreenWidget(null);
    } else if (
      currentScreenSize === 'sm' &&
      allOpenPanelsInOrder.length === SCREEN_SIZE_LIMITS[currentScreenSize]
    ) {
      // Code responsible for putting open sidebar to fullscreen
      const panelToFullscreen =
        allOpenPanelsInOrder[allOpenPanelsInOrder.length - 1];
      setFullscreenWidget(panelToFullscreen);
    }
  }, [
    fullscreenWidget,
    currentScreenSize,
    listOfPanelsToNames(allOpenPanelsInOrder),
  ]);

  useEffect(() => {
    if (fullscreenWidget != null && currentScreenSize !== 'sm') {
      // The panel was opened in "fullscreen" mode while screen size was 'sm'.
      // If that case happens - we need to unset the fullscreen mode
      setFullscreenWidget(null);
    }
  }, [currentScreenSize]);

  function setFullscreenAndClosePrevious(
    newFullscreen:
      | null
      | StickySidebarElementType<DetailPageItemType>
      | LeftSidebarElementType<DetailPageItemType>
  ) {
    return setFullscreenWidget((oldFullscreen) => {
      if (currentScreenSize === 'sm' && oldFullscreen != null) {
        closeSidebars([oldFullscreen]);
      }
      return newFullscreen;
    });
  }

  return {
    currentScreenSize,
    fullscreenWidget,
    setFullscreenWidget: setFullscreenAndClosePrevious,
  };
}
